import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  input,
  OnChanges,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import PreviewDocComponent from '@iupics-components/standard/preview-doc/preview-doc.component';
import { TranslateModule } from '@ngx-translate/core';
import * as pdfjs from 'pdfjs-dist';
import { PDF_WORKER_SRC } from '@iupics-util/tools/pdf-worker.util';
import { Subscription } from 'rxjs';
import { formatSize } from './documents-query';
import { DocumentFilesService } from './document-files.service';
import { DocumentsService } from './documents.service';

type PreviewKind = 'pdf' | 'image' | 'text' | 'xlsx' | 'none';

const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'ico', 'avif'];
const TEXT_EXT = ['txt', 'csv', 'xml', 'json', 'log', 'md', 'sh', 'sql', 'yml', 'yaml', 'properties', 'ini'];
/** above it the preview waits for a click (the download would be long) */
const AUTO_MAX = 40 * 1024 * 1024;
/** text shown at most (characters) */
const TEXT_MAX = 400_000;
/**
 * Fast preview of the Documents window: one authenticated download of the file as a Blob (no text decoding of
 * binaries, no base64 data URL), PDF pages rendered by pdf.js from the bytes, lazily (first page at once, the others
 * when scrolled to), at the panel width; images through an object URL; text files as text. Excel files use
 * iu-preview-doc (its parser), other types show the download fallback.
 */
@Component({
  selector: 'iu-document-preview',
  standalone: true,
  imports: [TranslateModule, forwardRef(() => PreviewDocComponent)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (state()) {
      @case ('loading') {
        <div class="dp-state" role="status">
          <i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>{{ 'documents.state.loading' | translate }}
          @if (progress() !== null) {
            <span class="dp-progress"><span class="dp-progress-bar" [style.width.%]="progress()"></span></span>
          }
        </div>
      }
      @case ('ask') {
        <div class="dp-state">
          <i class="fa-solid fa-file-arrow-down" aria-hidden="true"></i>
          <span>{{ 'documents.preview.large' | translate: { size: sizeLabel() } }}</span>
          <button type="button" class="p-button p-button-secondary dp-btn" (click)="load(true)">{{ 'documents.preview.show' | translate }}</button>
        </div>
      }
      @case ('error') {
        <div class="dp-state dp-state--error">
          <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>{{ 'documents.preview.error' | translate }}
          <button type="button" class="p-button p-button-secondary dp-btn" (click)="load(true)">{{ 'documents.actions.retry' | translate }}</button>
        </div>
      }
      @case ('none') {
        <div class="dp-state">
          <i class="fa-solid fa-eye-slash" aria-hidden="true"></i>{{ 'documents.preview.none' | translate }}
          <button type="button" class="p-button p-button-secondary dp-btn" (click)="download.emit()">{{ 'documents.actions.download' | translate }}</button>
        </div>
      }
    }
    @if (kind() === 'image' && objectUrl()) {
      <img class="dp-image" [src]="objectUrl()" [alt]="fileName()" (error)="state.set('error')" />
    }
    @if (kind() === 'text' && text() !== null) {
      <pre class="dp-text">{{ text() }}</pre>
      @if (textTruncated()) {
        <p class="dp-note">{{ 'documents.preview.truncated' | translate }}</p>
      }
    }
    @if (kind() === 'xlsx' && state() === 'ready') {
      <iu-preview-doc [urlFile]="xlsxUrl()" [fileName]="fileName()" [fromDocServer]="false" />
    }
    @if (kind() === 'pdf' && pages() > 0) {
      <p class="dp-note dp-pages">{{ 'documents.preview.pages' | translate: { count: pages() } }}</p>
    }
    <!-- always present: pages are drawn into it without waiting for a change detection -->
    <div #pdfPages class="dp-pages-list" [hidden]="kind() !== 'pdf'"></div>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
    }
    .dp-state {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-height: 160px;
      padding: 12px;
      color: var(--docs-ft-color-muted);
      text-align: center;
    }
    .dp-state--error {
      color: var(--docs-color-danger);
    }
    .p-button.dp-btn {
      height: 28px;
      margin: 0;
      padding: 0 10px;
      font-size: var(--font-size);
    }
    .dp-progress {
      display: block;
      width: 100%;
      max-width: 220px;
      height: 4px;
      overflow: hidden;
      border-radius: 2px;
      background: var(--docs-bg-color-hover);
    }
    .dp-progress-bar {
      display: block;
      height: 100%;
      background: var(--docs-color-accent);
    }
    .dp-image {
      display: block;
      max-width: 100%;
      max-height: 44vh;
      margin: 0 auto;
      object-fit: contain;
    }
    .dp-text {
      margin: 0;
      padding: 8px;
      font-size: 12px;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
    .dp-note {
      margin: 4px 8px;
      color: var(--docs-ft-color-muted);
      font-size: 11px;
    }
    .dp-pages-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 0 8px 8px;
    }
    :host ::ng-deep .dp-page {
      position: relative;
      width: 100%;
      background: #fff;
      box-shadow: 0 1px 3px #0003;
    }
    :host ::ng-deep .dp-page canvas {
      display: block;
      width: 100%;
      height: 100%;
    }
  `,
})
export class DocumentPreviewComponent implements OnChanges {
  readonly docId = input.required<string>();
  readonly fileName = input<string>('');
  readonly size = input<number | null>(null);
  /** a new version (same document): loaded again */
  readonly version = input(1);
  readonly download = output<void>();

  readonly #docs = inject(DocumentsService);
  readonly #files = inject(DocumentFilesService);
  readonly #cd = inject(ChangeDetectorRef);
  readonly #host = inject(ElementRef<HTMLElement>);

  @ViewChild('pdfPages', { static: true }) pdfPages: ElementRef<HTMLDivElement> | undefined;

  readonly state = signal<'loading' | 'ask' | 'ready' | 'error' | 'none'>('loading');
  readonly kind = signal<PreviewKind>('none');
  readonly progress = signal<number | null>(null);
  readonly objectUrl = signal<string | null>(null);
  readonly text = signal<string | null>(null);
  readonly textTruncated = signal(false);
  readonly pages = signal(0);
  readonly xlsxUrl = signal<string | null>(null);

  #sub: Subscription | null = null;
  #pdf: pdfjs.PDFDocumentProxy | null = null;
  #observer: IntersectionObserver | null = null;
  #seq = 0;
  #destroyed = false;
  #refreshQueued = false;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.#destroyed = true;
      this.#reset();
    });
  }

  sizeLabel(): string {
    return formatSize(this.size());
  }

  ngOnChanges(): void {
    this.load(false);
  }


  /** cache key: a new version is another file */
  #key(): string {
    return `${this.docId()}#${this.version()}`;
  }

  #ext(): string {
    const name = this.fileName() ?? '';
    const i = name.lastIndexOf('.');
    return i > 0 ? name.slice(i + 1).toLowerCase() : '';
  }

  /** force: load even when the file is large */
  load(force: boolean): void {
    this.#reset();
    const seq = ++this.#seq;
    const ext = this.#ext();
    const kind: PreviewKind =
      ext === 'pdf' ? 'pdf' : IMAGE_EXT.includes(ext) ? 'image' : TEXT_EXT.includes(ext) ? 'text' : ext === 'xlsx' ? 'xlsx' : 'none';
    this.kind.set(kind);
    if (kind === 'none') {
      this.state.set('none');
      return;
    }
    if (kind === 'xlsx') {
      // the Excel parser of iu-preview-doc downloads the file itself
      this.xlsxUrl.set(this.#docs.contentUrl(this.docId(), true));
      this.state.set('ready');
      return;
    }
    const size = this.size();
    if (!force && size !== null && size > AUTO_MAX && !this.#files.cached(this.#key())) {
      this.state.set('ask');
      return;
    }
    const cached = this.#files.cached(this.#key());
    if (cached) {
      this.#show(cached, seq);
      return;
    }
    this.state.set('loading');
    this.progress.set(null);
    this.#sub = this.#docs.contentWithProgress(this.docId()).subscribe({
      next: (e) => {
        if (seq !== this.#seq) return;
        if (e.blob) {
          this.#files.remember(this.#key(), e.blob);
          this.#show(e.blob, seq);
        } else if (e.total) {
          this.progress.set(Math.round((e.loaded / e.total) * 100));
          this.#refresh();
        }
      },
      error: () => {
        if (seq !== this.#seq) return;
        this.state.set('error');
        this.#refresh();
      },
    });
  }

  #show(blob: Blob, seq: number): void {
    const kind = this.kind();
    if (kind === 'image') {
      this.objectUrl.set(URL.createObjectURL(blob));
      this.state.set('ready');
    } else if (kind === 'text') {
      blob
        .slice(0, TEXT_MAX * 4)
        .text()
        .then((t) => {
          if (seq !== this.#seq) return;
          this.textTruncated.set(t.length > TEXT_MAX || blob.size > TEXT_MAX * 4);
          this.text.set(t.length > TEXT_MAX ? t.slice(0, TEXT_MAX) : t);
          this.state.set('ready');
          this.#refresh();
        });
      return;
    } else if (kind === 'pdf') {
      this.state.set('ready');
      this.#renderPdf(blob, seq);
    }
    this.#refresh();
  }

  /** Placeholders for every page (sized like page 1), page 1 drawn at once, the others when they come into view */
  async #renderPdf(blob: Blob, seq: number): Promise<void> {
    const container = this.pdfPages?.nativeElement;
    if (!container) return;
    try {
      pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
      const data = new Uint8Array(await blob.arrayBuffer());
      if (seq !== this.#seq) return;
      // isEvalSupported false: pdf.js 3.x compiles font code with eval (CVE-2024-4367, script run by a crafted PDF)
      const pdf = await pdfjs.getDocument({ data, isEvalSupported: false }).promise;
      if (seq !== this.#seq) {
        pdf.destroy();
        return;
      }
      this.#pdf = pdf;
      this.pages.set(pdf.numPages);
      this.#refresh();
      const first = await pdf.getPage(1);
      const base = first.getViewport({ scale: 1 });
      const ratio = base.height / base.width;
      const slots: HTMLDivElement[] = [];
      for (let n = 1; n <= pdf.numPages; n++) {
        const slot = document.createElement('div');
        slot.className = 'dp-page';
        slot.style.aspectRatio = `${1 / ratio}`;
        slot.dataset['page'] = String(n);
        container.appendChild(slot);
        slots.push(slot);
      }
      const root = (this.#host.nativeElement as HTMLElement).closest('.doc-preview') as HTMLElement | null;
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const slot = entry.target as HTMLDivElement;
            this.#observer?.unobserve(slot);
            this.#drawPage(pdf, Number(slot.dataset['page']), slot, seq);
          }
        },
        { root, rootMargin: '400px 0px' }
      );
      slots.forEach((s) => this.#observer!.observe(s));
    } catch {
      if (seq === this.#seq) {
        this.state.set('error');
        this.#refresh();
      }
    }
  }

  async #drawPage(pdf: pdfjs.PDFDocumentProxy, n: number, slot: HTMLDivElement, seq: number): Promise<void> {
    try {
      const page = await pdf.getPage(n);
      if (seq !== this.#seq) return;
      // panel width at the screen density (at most 2x): sharp without huge canvases
      const width = Math.max(200, slot.clientWidth) * Math.min(2, window.devicePixelRatio || 1);
      const viewport = page.getViewport({ scale: width / page.getViewport({ scale: 1 }).width });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const context = canvas.getContext('2d');
      if (!context) return;
      await page.render({ canvasContext: context, viewport }).promise;
      if (seq !== this.#seq) return;
      slot.style.aspectRatio = `${viewport.width / viewport.height}`;
      slot.replaceChildren(canvas);
      page.cleanup();
    } catch {
      // a page that fails stays blank: the others are still shown
    }
  }

  /**
   * Updates the view now: the specific window can sit in a view the application tick does not refresh after an
   * asynchronous answer (the preview only appeared on the next click), so markForCheck alone is not enough.
   */
  #refresh(): void {
    if (this.#destroyed) return;
    this.#cd.markForCheck();
    if (this.#refreshQueued) return;
    // in a microtask: never inside a running change detection, several updates in one pass
    this.#refreshQueued = true;
    queueMicrotask(() => {
      this.#refreshQueued = false;
      if (!this.#destroyed) this.#cd.detectChanges();
    });
  }

  #reset(): void {
    this.#sub?.unsubscribe();
    this.#sub = null;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#pdf?.destroy();
    this.#pdf = null;
    const url = this.objectUrl();
    if (url) URL.revokeObjectURL(url);
    this.objectUrl.set(null);
    this.text.set(null);
    this.textTruncated.set(false);
    this.pages.set(0);
    this.xlsxUrl.set(null);
    this.pdfPages?.nativeElement.replaceChildren();
  }
}
